import { useState, useCallback, useMemo } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { TelephoneNumber } from '#types';
import { formatTelephoneFromComponents } from '@/components/rendering/fields/formatTelephoneFromComponents';

/**
 * Props for useTelephoneUI hook
 */
interface UseTelephoneUIProps {
  /** Current telephone value (string or structured) */
  value: string | TelephoneNumber | null | undefined;
  /** Callback when telephone changes */
  onChange: (value: TelephoneNumber) => void;
}

/**
 * Custom hook managing telephone input state, parsing, and formatting.
 * Handles autocomplete input, manual edits, and sync with external value.
 * 
 * @param props - Hook configuration with value and onChange callback
 * @returns Telephone UI state and handlers
 */
export const useTelephoneUI = ({ value: rawValue, onChange }: UseTelephoneUIProps) => {
  const [localInput, setLocalInput] = useState<{ value: string; isTyping: boolean }>({ value: '', isTyping: false });
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [prevFormatted, setPrevFormatted] = useState<string | undefined>(undefined);

  const telephoneValue = useMemo(() => {
    if (!rawValue) return { formatted: '', components: {} };
    if (typeof rawValue === 'string') {
      return parseTelephone(rawValue);
    }
    return rawValue as TelephoneNumber;
  }, [rawValue]);

  const externalFormatted = telephoneValue.formatted || '';
  
  // Sync input when external value changes (not during active typing)
  if (prevFormatted !== externalFormatted) {
    setPrevFormatted(externalFormatted);
    if (!localInput.isTyping) {
      setLocalInput({ value: externalFormatted, isTyping: false });
    }
  }

  const inputValue = localInput.isTyping ? localInput.value : externalFormatted;

  const handleInputChange = useCallback((value: string) => {
    setLocalInput({ value, isTyping: true });
  }, []);

  const handleBlur = useCallback(() => {
    setLocalInput(prev => ({ ...prev, isTyping: false }));
    const parsed = parseTelephone(localInput.value);
    if (parsed.formatted !== telephoneValue.formatted) {
      onChange(parsed);
    }
  }, [localInput.value, telephoneValue.formatted, onChange]);

  const handleClear = useCallback(() => {
    setLocalInput({ value: '', isTyping: false });
    onChange({ formatted: '', components: {}, alternativeData: {} });
  }, [onChange]);

  const handleComponentChange = useCallback((field: keyof NonNullable<TelephoneNumber['components']>, newValue: string) => {
    const updatedComponents = {
      ...telephoneValue.components,
      [field]: newValue
    };

    const formatted = formatTelephoneFromComponents(updatedComponents);

    const newTelephoneValue: TelephoneNumber = {
      formatted,
      components: updatedComponents,
      alternativeData: telephoneValue.alternativeData
    };

    onChange(newTelephoneValue);
  }, [telephoneValue, onChange]);

  return {
    telephoneValue,
    inputValue,
    popoverOpen,
    setPopoverOpen,
    handleInputChange,
    handleBlur,
    handleClear,
    handleComponentChange
  };
};

/**
 * Parses a telephone string into structured components using libphonenumber-js.
 * Handles various international and national formats.
 * 
 * @param input - Raw telephone string input
 * @returns Parsed TelephoneNumber with components
 */
const parseTelephone = (input: string): TelephoneNumber => {
  if (!input || typeof input !== 'string') {
    return { formatted: '', components: {} };
  }

  const trimmed = input.trim();
  
  // Extract extension first (before parsing)
  const extensionMatch = trimmed.match(/(?:ext\.?|extension|x)\s*(\d+)$/i);
  const extension = extensionMatch ? extensionMatch[1] : undefined;
  const numberWithoutExt = extension ? trimmed.replace(/\s*(?:ext\.?|extension|x)\s*\d+$/i, '').trim() : trimmed;

  try {
    const digitsOnly = numberWithoutExt.replace(/[^\d]/g, '');
    
    // Try parsing with libphonenumber-js
    let phoneNumber = parsePhoneNumberFromString(numberWithoutExt);
    
    // If parsing failed, try assuming US for numbers that might be US format
    if (!phoneNumber) {
      // Check if it's a 10-digit number or 11-digit starting with 1
      if (digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly[0] === '1')) {
        try {
          // For 11-digit numbers starting with 1, add + prefix
          const normalizedNumber = digitsOnly.length === 11 && digitsOnly[0] === '1' 
            ? `+${digitsOnly}` 
            : numberWithoutExt;
          phoneNumber = parsePhoneNumberFromString(normalizedNumber, 'US');
        } catch {
          // Continue to fallback
        }
      }
    }
    
    if (phoneNumber) {
      const countryCode = phoneNumber.countryCallingCode ? `+${phoneNumber.countryCallingCode}` : undefined;
      const number = phoneNumber.nationalNumber;
      
      const formatted = formatTelephoneFromComponents({
        countryCode,
        number,
        extension
      });

      return {
        formatted,
        components: {
          countryCode,
          number,
          extension
        },
        alternativeData: {
          country: phoneNumber.country,
          e164: phoneNumber.number
        }
      };
    }
  } catch (error) {
    // Parsing failed, try fallback regex parsing
  }

  // Fallback: simple regex-based parsing
  const fallback = fallbackParseTelephone(numberWithoutExt, extension);
  return fallback;
};

/**
 * Fallback telephone parsing using regex patterns when libphonenumber-js fails.
 * 
 * @param input - Telephone string without extension
 * @param extension - Already extracted extension
 * @returns Parsed TelephoneNumber
 */
const fallbackParseTelephone = (input: string, extension?: string): TelephoneNumber => {
  const digitsOnly = input.replace(/[^\d]/g, '');
  
  // Extract country code if present
  let countryCode: string | undefined;
  let number: string;
  
  if (input.match(/^\+(\d{1,3})/)) {
    // Has explicit + prefix
    const countryCodeMatch = input.match(/^\+(\d{1,3})/);
    countryCode = countryCodeMatch ? `+${countryCodeMatch[1]}` : undefined;
    const withoutCountryCode = input.replace(/^\+\d{1,3}\s*/, '');
    number = withoutCountryCode.replace(/[^\d]/g, '');
  } else if (digitsOnly.length === 11 && digitsOnly[0] === '1') {
    // 11 digits starting with 1 - treat as US number with country code
    countryCode = '+1';
    number = digitsOnly.substring(1);
  } else {
    // No country code detected
    number = digitsOnly;
  }

  const formatted = formatTelephoneFromComponents({
    countryCode,
    number,
    extension
  });

  return {
    formatted,
    components: {
      countryCode,
      number,
      extension
    }
  };
};

